import { BarSeriesType, LineSeriesType } from '@mui/x-charts';
import { QueryKey, useSuspenseQueries } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import isEqual from 'lodash.isequal';
import { useReducer } from 'react';
import { useLocation } from 'react-router';
import { match } from 'ts-pattern';
import { useDebounce } from 'use-debounce';
import { QueryToSqlHelper } from '../../../../../api/query-to-sql-helper';
import { Hasher } from '../../../../api/hasher';
import { JsonLocalStorageService } from '../../../../api/local-storage-service';
import { MetricDetails } from '../../../../api/metrics/types';
import { hashString, MetricResult } from '../../../../api/types';
import {
  CohortMetricId,
  EmployeeCohortMetricId,
  MetricGroupId,
  MetricId,
  RegularMetricId,
} from '../../../../api/types-graphql';
import {
  getMetricTypeFromMetrics,
  metricIdNeedsEmptyCohortFilter,
  toMetricResultOrError,
} from '../../../../api/utils';
import { Languages } from '../../../../constants';
import {
  useGlobalLocaleContext,
  useMetricDetailsMapContext,
  useMetricsQueryServiceContext,
} from '../../../../context/contexts';
import { reducerWithPersistentWrapper } from '../../../../context/hooks';
import { GlobalDisplayState } from '../../../../context/types';
import {
  Colors,
  DataFieldWithDataType,
  MetricIdType,
  MetricIdTypeArrays,
  Segmentation,
  TimeSelection,
} from '../../../../types';
import { mergeSegmentations, removeDuplicates } from '../../../utils';
import {
  AreaChartConfig,
  ChartTypeConfig,
  LineChartConfig,
  LineChartQueryConfig,
  OverTimeBarChartConfig,
  OverTimeBarChartQueryConfig,
  OverTimeTableConfig,
} from '../../dashboards/types';
import { Filter, Segment } from '../../filter/filterbar/types';
import { ItemsState } from '../../filter/types';
import { toSegmentation } from '../../filter/utils';
import { NonSegmentedTableOverTimeData } from '../../tableview/types';
import {
  NonSegmentedOverTimeTableDataColumnWiseMonoid,
  NonSegmentedOverTimeTableDataMonoid,
} from '../../tableview/utils';
import { TimeSliderState } from '../../timeslider/types';
import { useGetMetricDisplayName } from '../hooks';
import { MuiOverTimeChartData } from '../mui-charts/types';
import { GlobalDisplayActionWithGlobalDisplayState } from '../types';
import {
  DisplayState,
  LocalOverTimeDisplayAction,
  OverTimeDisplayHandle,
  QueryConfig,
  SegmentationsToToolsSync,
  UseMetricQueriesReturnType,
} from './types';
import {
  getYAxisConfig,
  timeSliderStateToOverTimeTimeSelection,
} from './utils';

const isStackedBarChart = (state: DisplayState) => {
  return match(state.chartTypeConfig)
    .with(
      { chartType: 'bar' },
      () => state.metrics.length === 1 && !!state.segmentations
    )
    .otherwise(() => false);
};

export const getInitialState = (
  segmentations: Segmentation[] | undefined,
  metrics: MetricIdType[],
  chartTypeConfig: ChartTypeConfig,
  displayGlobalState: GlobalDisplayState
): DisplayState => {
  const {
    enablePercentage: enablePercentageGlobal,
    showPercentage: showPercentageGlobal,
    enableStack: enableStackGlobal,
    showStack: showStackGlobal,
    enableLabels: enableLabelsGlobal,
    showLabels: showLabelsGlobal,
    enableLegend: enableLegendGlobal,
    showLegend: showLegendGlobal,
  } = displayGlobalState;
  const initialState: DisplayState = {
    enableLabels: enableLabelsGlobal,
    showLabels: showLabelsGlobal,
    enableLegend: enableLegendGlobal,
    showLegend: showLegendGlobal,
    showTableView: false,
    enablePercentage: enablePercentageGlobal,
    showPercentage: showPercentageGlobal,
    enableStack: enableStackGlobal,
    showStack: showStackGlobal,
    segmentations,
    metrics,
    chartTypeConfig,
    globalDisplayState: displayGlobalState,
  };
  return initialState;
};
export const useDisplayWithPersistence = (
  chartId: string,
  chartTypeConfig: ChartTypeConfig,
  segmentations: Segmentation[] | undefined,
  metrics: MetricIdType[],
  displayGlobalState: GlobalDisplayState
): OverTimeDisplayHandle => {
  const initialState = getInitialState(
    segmentations,
    metrics,
    chartTypeConfig,
    displayGlobalState
  );
  const location = useLocation();
  const persistenceKey = `${location.pathname}-${chartId}-display`;
  const localStorageService = new JsonLocalStorageService<DisplayState>(
    persistenceKey
  );
  const storedItems = localStorageService.get();
  const state = storedItems ?? initialState;
  return useReducer(
    reducerWithPersistentWrapper(reducer, localStorageService),
    state
  );
};

export const reducer = (
  state: DisplayState,
  action:
    | GlobalDisplayActionWithGlobalDisplayState
    | LocalOverTimeDisplayAction
    | SegmentationsToToolsSync
): DisplayState => {
  const stackedBarChart = isStackedBarChart(state);
  const newState = match(action)
    .with(
      { type: 'segmentations-to-tools-sync' },
      (a: SegmentationsToToolsSync) => {
        const stackedBarChart = isStackedBarChart({
          ...state,
          segmentations: a.segmentations,
        });
        const enablePercentage =
          stackedBarChart &&
          !state.showTableView &&
          state.globalDisplayState.enablePercentage;
        const enableStack =
          stackedBarChart &&
          !state.showTableView &&
          !state.showPercentage &&
          state.globalDisplayState.enableStack;
        const showStack = enableStack && state.showStack;
        return {
          ...state,
          enablePercentage,
          enableStack,
          showStack,
          segmentations: a.segmentations,
        };
      }
    )
    .with({ type: 'toggle-labels' }, () => {
      return {
        ...state,
        showLabels: !state.showLabels,
      };
    })
    .with({ type: 'toggle-percentage' }, () => {
      const showPercentage = stackedBarChart && !state.showPercentage;
      return {
        ...state,
        showPercentage,
        enableStack: !showPercentage,
      };
    })
    .with({ type: 'toggle-stack' }, () => {
      const showStack = stackedBarChart && !state.showStack;
      return {
        ...state,
        showStack,
        enablePercentage: !showStack,
      };
    })
    .with({ type: 'toggle-legend' }, () => {
      return {
        ...state,
        showLegend: !state.showLegend,
      };
    })
    .with({ type: 'toggle-tableView' }, () => {
      const enablePercentage =
        stackedBarChart &&
        state.showTableView &&
        state.globalDisplayState.enablePercentage;
      const enableStack =
        stackedBarChart &&
        state.showTableView &&
        !state.showPercentage &&
        state.globalDisplayState.enableStack;
      const enableLabels = state.showTableView;
      const enableLegend = state.showTableView;
      return {
        ...state,
        showTableView: !state.showTableView,
        enablePercentage,
        enableStack,
        enableLabels,
        enableLegend,
      };
    })
    .with({ type: 'global-state-sync' }, (a) => {
      const getDisplayStateValue = (
        localStateValue: boolean,
        globalDisplayKey: keyof GlobalDisplayState
      ) => {
        const shouldChange =
          state.globalDisplayState[globalDisplayKey] !==
          a.globalDisplayState[globalDisplayKey];
        return shouldChange
          ? a.globalDisplayState[globalDisplayKey]
          : localStateValue;
      };

      const showLabels = getDisplayStateValue(state.showLabels, 'showLabels');
      const showPercentage = getDisplayStateValue(
        state.showPercentage,
        'showPercentage'
      );
      const enablePercentage = getDisplayStateValue(
        state.enablePercentage,
        'enablePercentage'
      );
      const showStack = getDisplayStateValue(state.showStack, 'showStack');
      const enableStack = getDisplayStateValue(
        state.enableStack,
        'enableStack'
      );
      const showLegend = getDisplayStateValue(state.showLegend, 'showLegend');

      const finalShowPercentage = stackedBarChart && showPercentage;
      const finalShowStack = stackedBarChart && !showPercentage && showStack;
      const finalEnablePercentage =
        stackedBarChart && !state.showTableView && enablePercentage;
      const finalEnableStack =
        stackedBarChart && !showPercentage && enableStack;

      return {
        ...state,
        showLabels,
        showPercentage: finalShowPercentage,
        showStack: finalShowStack,
        showLegend,
        enablePercentage: finalEnablePercentage,
        enableStack: finalEnableStack,
        globalDisplayState: a.globalDisplayState,
      };
    })
    .exhaustive();
  return { ...newState };
};

export const useMetricQueries = <T extends LineSeriesType | BarSeriesType>(
  chartId: string,
  queries: LineChartQueryConfig[] | OverTimeBarChartQueryConfig[],
  timeSliderState: TimeSliderState | null,
  filtersState: ItemsState<Filter>,
  segmentationState: ItemsState<Segment>,
  manualDataRefetchCount: number,
  chartTypeConfig:
    | LineChartConfig
    | AreaChartConfig
    | OverTimeBarChartConfig
    | OverTimeTableConfig,
  displayGlobalState: GlobalDisplayState,
  formatLabel: (
    value: (string | null)[],
    dataField: DataFieldWithDataType
  ) => string,
  colors: Colors | undefined,
  t: TFunction,
  processData: (
    resultOrErrors: Array<MetricResult | Error>,
    xAxisId: number,
    isBenchmark: boolean | undefined,
    segmentations: Segmentation[] | undefined,
    displayState: DisplayState,
    metricDetailsMap: Record<
      RegularMetricId | CohortMetricId | EmployeeCohortMetricId,
      MetricDetails
    >,
    formatLabel: (
      value: (string | null)[],
      datafield: DataFieldWithDataType
    ) => string,
    getMetricDisplayName: (
      metricId: RegularMetricId | CohortMetricId | EmployeeCohortMetricId,
      metricGroupId: MetricGroupId
    ) => string,
    locale: Languages,
    colors: Colors | undefined,
    t: TFunction,
    availableWidth?: number
  ) => MuiOverTimeChartData<T>,
  availableWidth?: number
): UseMetricQueriesReturnType<T> => {
  const metricService = useMetricsQueryServiceContext();
  const locale = useGlobalLocaleContext();
  const getMetricDisplayName = useGetMetricDisplayName();
  const metricDetailsMap = useMetricDetailsMapContext();
  const queryConfigs = queries.reduce<QueryConfig[]>((acc, query) => {
    const {
      metrics,
      timeSelection,
      filters,
      segmentations,
      xAxisId,
      isBenchmark,
    } = query;

    const finalTimeSelection: TimeSelection = timeSliderState
      ? timeSliderStateToOverTimeTimeSelection(timeSliderState)
      : timeSelection;
    const metricType = getMetricTypeFromMetrics(metrics);
    // Currently, we are assuming that all metrics in the query are of the same type
    // Even without this code below, that implicit assumption is being made
    // TODO: Improve typing to avoid need for such checks
    const firstMetric = metrics[0];
    const needsEmptyCohortFilter = metricIdNeedsEmptyCohortFilter(
      firstMetric.value as unknown as MetricId
    );

    const allSegmentations = mergeSegmentations(
      segmentations,
      toSegmentation(segmentationState.items, metricType),
      metrics,
      1
    );

    // TODO: @Alex: can we assume metrics.length is always non zero
    // Also assuming here that all type(regular, cohort or empCohort) for all
    // metrics within the same query is the same. Need to enforce this somehow later.
    // For now, I am doing this validateMetrics step just for peace of mind
    const validateMetrics = (metrics: MetricIdTypeArrays) => {
      if (!metrics.length) {
        throw new Error('Error: query.metrics array is empty.');
      }
      const uniqueMetricTypes = removeDuplicates(metrics.map((m) => m.type));
      if (uniqueMetricTypes.length > 1) {
        throw new Error(
          `Error: query.metrics contains metrics of different types - ${uniqueMetricTypes.join(' ,')}`
        );
      }
    };
    validateMetrics(metrics);
    const sqlFiltersFromFilterState = QueryToSqlHelper.getFilterConditions(
      filtersState.items,
      metricType,
      needsEmptyCohortFilter
    );
    const allFilters =
      [sqlFiltersFromFilterState, filters]
        .flatMap((f) => (f ? [f] : []))
        .join(' AND ') || undefined;
    const key: QueryKey = [
      JSON.stringify([
        `over-time`,
        hashString([
          ...metricService.getDeps(),
          Hasher.hash(metrics),
          Hasher.hash(finalTimeSelection),
          Hasher.hash(allFilters),
          Hasher.hash(allSegmentations),
        ]),
        manualDataRefetchCount,
      ]),
    ];
    const queryFn = () =>
      toMetricResultOrError(
        metricService,
        metrics,
        finalTimeSelection,
        allFilters,
        allSegmentations
      );
    return [
      ...acc,
      {
        queryKey: key,
        queryFn,
        metrics,
        segmentations: allSegmentations ?? undefined,
        filters: allFilters,
        timeSelection: finalTimeSelection,
        xAxisId,
        isBenchmark,
      },
    ];
  }, []);

  const totalSegmentations = queryConfigs.reduce<Segmentation[] | undefined>(
    (acc, qc) => mergeSegmentations(acc, qc.segmentations, qc.metrics, 2),
    undefined
  );
  const totalMetrics = queryConfigs.flatMap<MetricIdType>((qc) => qc.metrics);
  const totalFilters = queryConfigs
    .flatMap((qc) => qc.filters)
    .filter((f) => !!f)
    .join(' AND ');

  const displayHandle = useDisplayWithPersistence(
    chartId,
    chartTypeConfig,
    totalSegmentations,
    totalMetrics,
    displayGlobalState
  );
  const [displayState] = displayHandle;

  const [queriesToFetch] = useDebounce(
    queryConfigs.map((qc) => ({ queryKey: qc.queryKey, queryFn: qc.queryFn })),
    500,
    {
      equalityFn: (prev, next) =>
        isEqual(
          prev.map((p) => p.queryKey),
          next.map((n) => n.queryKey)
        ),
    }
  );

  const { data: results, error } = useSuspenseQueries({
    queries: queriesToFetch,
    combine: (results) => {
      return {
        data: results,
        error: results.flatMap((r) => (r.error ? [r.error] : [])),
      };
    },
  });
  const tableViewDataRowWiseMonoid = new NonSegmentedOverTimeTableDataMonoid();
  const tableViewDataColumnWiseMonoid =
    new NonSegmentedOverTimeTableDataColumnWiseMonoid();

  const data = results.reduce<MuiOverTimeChartData<T>>((acc, r, i) => {
    const query = queryConfigs[i];
    const tableViewDataMonoid = query.isBenchmark
      ? tableViewDataColumnWiseMonoid
      : tableViewDataRowWiseMonoid;

    const muiChartsData: MuiOverTimeChartData<T> = processData(
      r.data,
      query.xAxisId ?? 0,
      query.isBenchmark,
      query.segmentations,
      displayState,
      metricDetailsMap,
      formatLabel,
      getMetricDisplayName,
      locale.selected,
      colors,
      t,
      availableWidth
    );
    // TODO: This works only for non segmented which isn't good
    const combinedTableViewData = tableViewDataMonoid.combine(
      (acc.tableViewData ??
        tableViewDataMonoid.empty) as NonSegmentedTableOverTimeData<MetricIdType>,
      muiChartsData.tableViewData as NonSegmentedTableOverTimeData<MetricIdType>
    );
    const combinedXAxisConfig = [
      ...(acc.xAxisConfig ?? []),
      ...(muiChartsData.xAxisConfig ?? []),
    ];

    // Combine all series first
    const combinedSeries = [...(acc.series ?? []), ...muiChartsData.series];

    // Recalculate Y-axis configs based on ALL combined series data
    // This ensures ticks cover the full range across all series
    const recalculatedYAxisConfig = getYAxisConfig(
      combinedSeries,
      displayState.showPercentage
    );

    // Preserve the original valueFormatter from the first config for each Y-axis
    const combinedYAxisConfig = recalculatedYAxisConfig.map((recalculated) => {
      const originalConfig =
        acc.yAxisConfig?.find((c) => c.id === recalculated.id) ??
        muiChartsData.yAxisConfig?.find((c) => c.id === recalculated.id);
      return {
        ...recalculated,
        valueFormatter:
          originalConfig?.valueFormatter ?? recalculated.valueFormatter,
      };
    });

    const result = {
      ...acc,
      series: combinedSeries,
      tableViewData: combinedTableViewData,
      barLabelConfig: muiChartsData.barLabelConfig,
      metricSql: [...(acc.metricSql ?? []), ...muiChartsData.metricSql],
      // Should make sure this yAxis and xAxis stuff doesn't impact other charts on AlexDB
      yAxisConfig: combinedYAxisConfig,
      xAxisConfig: combinedXAxisConfig,
    };

    return result;
  }, {} as MuiOverTimeChartData<T>);
  return {
    data,
    displayHandle,
    metrics: totalMetrics,
    segmentations: totalSegmentations,
    filters: totalFilters,
    timeSelection: queryConfigs[0].timeSelection,
    error,
    // ...queryConfigs,
  };
};
